import { useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { Optional } from 'packages/types'
import { actions } from 'builder/modules/resumeEditor'
import { ResumeValidationStatus } from 'builder/modules/constants'
import { useConfig } from 'builder/hooks/useConfig'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'

export const useResumePrefill = () => {
  const config = useConfig()
  const isAIResumeDraftEnabled = config?.features.aiResumeDraft
  const isUseExamplesEnabled = config?.features.startWithExamples
  const isUploadResumeEnabled = config?.features.uploadResume
  const isStartWithLinkedinEnabled = config?.features.startWithLinkedin
  const dispatch = useDispatch()
  const { resumeExamples, fetchingResumeExamples } = useTypedSelector(
    store => store.resumeEditor.useExample,
  )
  const hasResumeExamples = !!resumeExamples.length

  const handleCreateWithAi = useCallback(() => {
    if (!isAIResumeDraftEnabled) {
      return
    }
    dispatch(actions.resetAIResumeData())
    dispatch(actions.setShowAIResumeModal(true))
  }, [dispatch, isAIResumeDraftEnabled])

  const handleUseExample = useCallback(() => {
    if (!isUseExamplesEnabled) {
      return
    }
    if (hasResumeExamples) {
      dispatch(actions.setShowUseExamplePopup(true))
    } else if (!fetchingResumeExamples) {
      dispatch(actions.fetchResumeExamples())
    }
  }, [dispatch, fetchingResumeExamples, hasResumeExamples, isUseExamplesEnabled])

  const handleResumeUpload = useCallback(
    (selectedResume: Optional<File>) => {
      if (!isUploadResumeEnabled || !selectedResume) {
        return
      }
      dispatch(
        actions.updateSimpleField({
          name: 'showPrefillResumeModal',
          value: true,
          debounce: false,
        }),
      )
      dispatch(actions.setResumeValidationStatus(ResumeValidationStatus.uploading))
      dispatch(actions.setResumeForPrefill(selectedResume))
    },
    [dispatch, isUploadResumeEnabled],
  )

  const handleStartWithLinkedIn = useCallback(() => {
    if (!isStartWithLinkedinEnabled) {
      return
    }
    dispatch(
      actions.updateCreateResumeWithLinkedInState({
        showCreateResumeWithLinkedInModal: true,
      }),
    )
  }, [dispatch, isStartWithLinkedinEnabled])

  return {
    hasResumeExamples,
    fetchingResumeExamples,
    handleCreateWithAi,
    handleUseExample,
    handleResumeUpload,
    handleStartWithLinkedIn,
  }
}
